import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';
import API from '../api';
import FilterSidebar from '../components/FilterSidebar';
import { MdViewModule, MdViewList } from 'react-icons/md';
import { useEventTracking } from '../hooks/useAnalytics';
import { CategorySkeleton } from '../components/skeleton';

const sortOptions = [
  { value: '', label: 'Rilevanza' },
  { value: 'price_asc', label: 'Prezzo: dal più basso' },
  { value: 'price_desc', label: 'Prezzo: dal più alto' },
  { value: 'title_asc', label: 'Titolo A-Z' },
  { value: 'title_desc', label: 'Titolo Z-A' },
];

const SearchResults = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get('query') || '';
  const category = queryParams.get('category') || '';
  const { trackSearch, trackFilter } = useEventTracking();
  const [sort, setSort] = useState('');
  const [view, setView] = useState('grid');
  const [filters, setFilters] = useState({
    sort: '',
    price: [0, 0],
    brands: [],
    category: category && category !== 'Tutti' ? category : '',
    vendors: [],
  });
  const [filterData, setFilterData] = useState({
    min_price: 0,
    max_price: 0,
    brands: [],
    vendors: [],
  });
  const [filtersReady, setFiltersReady] = useState(false);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllVendors, setShowAllVendors] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(null);

  // Aggiorna i filtri quando cambia l'URL (query o categoria)
  useEffect(() => {
    const newQueryParams = new URLSearchParams(location.search);
    const newQuery = newQueryParams.get('query') || '';
    const newCategory = newQueryParams.get('category') || '';
    
    // Aggiorna il filtro categoria quando cambia l'URL
    setFilters(prev => ({
      ...prev,
      category: newCategory && newCategory !== 'Tutti' ? newCategory : '',
    }));
  }, [location.search]);

  // Carica i filtri da API
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await API.get('/search/filters', {
          params: { query, category: category && category !== 'Tutti' ? category : undefined },
        });
        if (res.data.success) {
          setFilterData({
            min_price: res.data.min_price || 0,
            max_price: res.data.max_price || 0,
            brands: res.data.brands || [],
            vendors: res.data.vendors || [],
          });
          setFilters(f => ({
            ...f,
            price: [res.data.min_price || 0, res.data.max_price || 0],
            brands: [],
            vendors: [],
          }));
        }
      } catch (e) {
        // fallback
      } finally {
        setFiltersReady(true);
      }
    };
    fetchFilters();
  }, [query, category]);

  // Recupera il nome leggibile della categoria se selezionata
  useEffect(() => {
    if (category && category !== 'Tutti') {
      API.get(`/categories/by-handle/${category}`)
        .then(res => {
          if (res.data && res.data.category && res.data.category.category_title) {
            setCategoryTitle(res.data.category.category_title);
          } else {
            setCategoryTitle(category);
          }
        })
        .catch(() => setCategoryTitle(category));
    } else {
      setCategoryTitle('');
    }
  }, [category]);

  // Traccia la ricerca quando la pagina si carica
  useEffect(() => {
    if (query) {
      trackSearch(query, 0); // Il numero di risultati verrà aggiornato quando ProductGrid carica
    }
  }, [query, trackSearch]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFilters((prev) => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter((v) => v !== value),
      }));
      
      // Traccia l'applicazione del filtro
      if (checked) {
        trackFilter(name, value);
      }
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      
      // Traccia l'applicazione del filtro
      if (value) {
        trackFilter(name, value);
      }
    }
  };

  const handlePriceChange = (e, idx) => {
    const val = Number(e.target.value);
    setFilters((prev) => {
      const newPrice = [...prev.price];
      newPrice[idx] = val;
      return { ...prev, price: newPrice };
    });
  };

  const handleSortChange = (e) => {
    setFilters((prev) => ({ ...prev, sort: e.target.value }));
  };

  // Applica i filtri dal modal mobile
  const handleApplyMobileFilters = () => {
    if (pendingFilters) setFilters(pendingFilters);
    setShowMobileFilters(false);
  };

  return (
    <div className="main-content-container search-results-page">
      <div className="search-results-header">
        <div className="search-results-title-section">
          <h2 className="search-results-title">
            Risultati per: "{query}"
            {category && category !== 'Tutti' && categoryTitle && (
              <span> nella categoria: <strong>{categoryTitle}</strong></span>
            )}
          </h2>
        </div>
      </div>
      <div className="search-results-content">
        {/* Sidebar filtri (desktop) */}
        <div className="search-results-sidebar">
          <FilterSidebar
            filterData={filterData}
            filters={filters}
            onFilterChange={handleFilterChange}
            onPriceChange={handlePriceChange}
            showAllBrands={showAllBrands}
            setShowAllBrands={setShowAllBrands}
            showAllVendors={showAllVendors}
            setShowAllVendors={setShowAllVendors}
          />
        </div>
        {/* Modal filtri mobile */}
        {showMobileFilters && (
          <div className="mobile-filters-modal">
            <div className="mobile-filters-overlay" onClick={() => setShowMobileFilters(false)} />
            <div className="mobile-filters-sheet">
              <FilterSidebar
                filterData={filterData}
                filters={pendingFilters || filters}
                onFilterChange={e => {
                  const { name, value, type, checked } = e.target;
                  setPendingFilters(prev => {
                    if (!prev) return prev;
                    if (type === 'checkbox') {
                      return {
                        ...prev,
                        [name]: checked
                          ? [...(Array.isArray(prev[name]) ? prev[name] : []), value]
                          : (Array.isArray(prev[name]) ? prev[name] : []).filter((v) => v !== value),
                      };
                    } else {
                      return { ...prev, [name]: value };
                    }
                  });
                }}
                onPriceChange={(e, idx) => {
                  const val = Number(e.target.value);
                  setPendingFilters(prev => {
                    if (!prev) return prev;
                    const newPrice = [...prev.price];
                    newPrice[idx] = val;
                    return { ...prev, price: newPrice };
                  });
                }}
                showAllBrands={showAllBrands}
                setShowAllBrands={setShowAllBrands}
                showAllVendors={showAllVendors}
                setShowAllVendors={setShowAllVendors}
                isMobile={true}
                onApply={handleApplyMobileFilters}
                onClose={() => setShowMobileFilters(false)}
              />
            </div>
          </div>
        )}
        {/* Griglia prodotti */}
        <div className="search-results-main">
          {/* Toolbar mobile (max-width: 991px) */}
          <div className="products-toolbar toolbar-mobile-custom">
            <div className="toolbar-mobile-row1">
              <button
                className="filter-btn"
                onClick={() => {
                  setPendingFilters({ ...filters });
                  setShowMobileFilters(true);
                }}
              >
                <span className="filter-icon" /> Filtri
              </button>
            </div>
            <div className="toolbar-mobile-row2">
              <div className="sort-pill">
                <select
                  className="sort-select"
                  name="sort"
                  value={filters.sort}
                  onChange={handleSortChange}
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className="sort-arrow">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M6 8l4 4 4-4" stroke="#c93249" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </div>
              <div className="view-toggle-pill">
                <div className="view-toggle-group">
                  <button
                    className={`view-toggle${view === 'grid' ? ' active' : ''}`}
                    onClick={() => setView('grid')}
                    type="button"
                    aria-label="Vista griglia"
                  >
                    <MdViewModule size={22} />
                  </button>
                  <button
                    className={`view-toggle${view === 'list' ? ' active' : ''}`}
                    onClick={() => setView('list')}
                    type="button"
                    aria-label="Vista lista"
                  >
                    <MdViewList size={22} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Toolbar desktop (min-width: 992px) */}
          <div className="products-toolbar toolbar-desktop-only">
            <label className="sort-label">Ordina per</label>
            <div className="sort-pill">
              <select
                className="sort-select"
                name="sort"
                value={filters.sort}
                onChange={handleSortChange}
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="sort-arrow">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M6 8l4 4 4-4" stroke="#c93249" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>
            <div className="view-toggle-pill">
              <div className="view-toggle-group">
                <button
                  className={`view-toggle${view === 'grid' ? ' active' : ''}`}
                  onClick={() => setView('grid')}
                  type="button"
                  aria-label="Vista griglia"
                >
                  <MdViewModule size={22} />
                </button>
                <button
                  className={`view-toggle${view === 'list' ? ' active' : ''}`}
                  onClick={() => setView('list')}
                  type="button"
                  aria-label="Vista lista"
                >
                  <MdViewList size={22} />
                </button>
              </div>
            </div>
          </div>
          {filtersReady && (
            <ProductGrid
              mode="search"
              searchQuery={query}
              filters={filters}
              view={view}
              onResultsLoaded={(totalResults) => {
                // Aggiorna il tracciamento della ricerca con il numero di risultati
                if (query) {
                  trackSearch(query, totalResults);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;